import { Camera, Mail, Phone, LucideIcon, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { FaFacebook, FaInstagram, FaTwitter, FaYoutube, FaLinkedin, FaGithub, FaWhatsapp } from "react-icons/fa";
import { getOptimizedImageUrl } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useLocalized } from "@/hooks/useLocalized";
import { LocalizedContent } from "@/types";

interface Profile {
  display_name: string;
  location?: LocalizedContent;
  phone?: string;
  email?: string;
  logo_url?: string;
}

interface SocialLink {
  id: string;
  platform_name: string;
  platform_url: string;
  icon_name: string;
}

interface FooterSettings {
  about_text?: LocalizedContent;
  copyright_text?: LocalizedContent;
  quick_links?: Array<{ label: string; url: string }>;
  contact_info?: {
    address?: string;
    email?: string;
    phone?: string;
  };
}

interface Service {
  id: string;
  name: LocalizedContent;
  category: string;
}

export function Footer() {
  const { t } = useTranslation();
  const { getLocalized } = useLocalized();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [footerSettings, setFooterSettings] = useState<FooterSettings | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchData = async () => {
      // Fetch profile
      const { data: profileData } = await supabase
        .from("photographer_profiles")
        .select("display_name, email, phone, location, logo_url")
        .single();

      if (profileData) {
        setProfile(profileData);
      }

      // Fetch social links
      const { data: linksData } = await supabase
        .from("social_media_links")
        .select("id, platform_name, platform_url, icon_name")
        .eq("is_active", true)
        .order("sort_order");

      if (linksData) {
        setSocialLinks(linksData);
      }

      // Fetch footer settings
      const { data: settingsData } = await supabase
        .from('footer_settings')
        .select('*')
        .eq('is_active', true)
        .single();

      if (settingsData) {
        setFooterSettings({
          about_text: settingsData.about_text,
          copyright_text: settingsData.copyright_text,
          quick_links: settingsData.quick_links as Array<{ label: string; url: string }>,
          contact_info: settingsData.contact_info as any
        });
      }

      // Fetch services
      const { data: servicesData } = await supabase
        .from('services')
        .select('id, name, category')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('category')
        .limit(5);

      if (servicesData) {
        setServices(servicesData);
      }
    };

    fetchData();
  }, []);

  return (
    <footer className="bg-background border-t">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {/* Brand */}
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center gap-2 sm:gap-3">
              {profile?.logo_url ? (
                <img
                  src={getOptimizedImageUrl(profile.logo_url, { height: 64, resize: 'contain' })}
                  alt="Logo"
                  className="h-6 w-6 sm:h-8 sm:w-8 object-contain"
                />
              ) : (
                <Camera className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              )}
              <span className="text-lg sm:text-xl font-bold">{profile?.display_name || "Photography"}</span>
            </div>
            <p className="text-sm sm:text-base text-muted-foreground">
              {getLocalized(footerSettings?.about_text) || t('footer.defaultAbout', "Capturing life's precious moments with artistic vision and professional excellence.")}
            </p>
            <div className="flex gap-3 sm:gap-4">
              {socialLinks.map((link) => {
                // Map of supported icons
                const IconMap: Record<string, any> = {
                  Facebook: FaFacebook,
                  Instagram: FaInstagram,
                  Twitter: FaTwitter,
                  Youtube: FaYoutube,
                  Linkedin: FaLinkedin,
                  Github: FaGithub,
                  WhatsApp: FaWhatsapp,
                  Link: Icons.Link,
                };

                // Try to find icon in map, fallback to Lucide, then generic Link
                // improved icon logic
                const getIcon = (link: SocialLink) => {
                  const platform = link.platform_name.toLowerCase();
                  const iconName = link.icon_name;

                  // 1. Check direct FontAwesome icon name matches (e.g. FaWhatsapp)
                  if (iconName.startsWith('Fa') && (IconMap as any)[iconName]) {
                    return (IconMap as any)[iconName];
                  }

                  // 2. Check platform name mapping
                  if (platform.includes('whatsapp')) return FaWhatsapp;
                  if (platform.includes('facebook')) return FaFacebook;
                  if (platform.includes('instagram')) return FaInstagram;
                  if (platform.includes('twitter') || platform.includes(' x')) return FaTwitter;
                  if (platform.includes('youtube')) return FaYoutube;
                  if (platform.includes('linkedin')) return FaLinkedin;
                  if (platform.includes('github')) return FaGithub;

                  // 3. Fallback to existing map (for legacy names like "Facebook")
                  if (IconMap[iconName]) return IconMap[iconName];

                  // 4. Fallback to Lucide (for existing "MessageCircle", "Camera", etc)
                  return (Icons as any)[iconName] as LucideIcon || Icons.Link;
                };

                const IconComponent = getIcon(link);

                return (
                  <a
                    key={link.id}
                    href={link.platform_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                    title={link.platform_name}
                  >
                    <IconComponent className="h-4 w-4 sm:h-5 sm:w-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Services */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-base sm:text-lg">{t('footer.services', 'Services')}</h3>
            <ul className="space-y-1.5 sm:space-y-2 text-sm sm:text-base text-muted-foreground">
              {services.length > 0 ? (
                services.map((service) => (
                  <li key={service.id}>
                    <Link to="/services" className="hover:text-primary transition-colors">
                      {getLocalized(service.name)}
                    </Link>
                  </li>
                ))
              ) : (
                <li><Link to="/services" className="hover:text-primary transition-colors">{t('footer.viewAllServices', 'View All Services')}</Link></li>
              )}
            </ul>
          </div>

          {/* Quick Links */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-base sm:text-lg">{t('footer.quickLinks', 'Quick Links')}</h3>
            <ul className="space-y-1.5 sm:space-y-2 text-sm sm:text-base text-muted-foreground">
              {footerSettings?.quick_links && footerSettings.quick_links.length > 0 ? (
                footerSettings.quick_links.map((link, index) => {
                  // Check if it's an anchor link
                  const isAnchorLink = link.url.startsWith('#') || link.url.startsWith('/#');
                  const anchorId = isAnchorLink ? link.url.replace(/^\/?#/, '') : null;

                  return (
                    <li key={index}>
                      {link.url.startsWith('http') ? (
                        <a href={link.url} className="hover:text-primary transition-colors" target="_blank" rel="noopener noreferrer">
                          {link.label}
                        </a>
                      ) : isAnchorLink && anchorId ? (
                        <Link
                          to="/"
                          state={{ scrollTo: anchorId }}
                          className="hover:text-primary transition-colors"
                        >
                          {link.label}
                        </Link>
                      ) : (
                        <Link to={link.url} className="hover:text-primary transition-colors">
                          {link.label}
                        </Link>
                      )}
                    </li>
                  );
                })
              ) : (
                <>
                  <li><Link to="/" className="hover:text-primary transition-colors">{t('nav.home', 'Home')}</Link></li>
                  <li><Link to="/portfolio" className="hover:text-primary transition-colors">{t('nav.portfolio', 'Portfolio')}</Link></li>
                  <li><Link to="/services" className="hover:text-primary transition-colors">{t('nav.services', 'Services')}</Link></li>
                  <li>
                    <Link
                      to="/"
                      state={{ scrollTo: 'contact' }}
                      className="hover:text-primary transition-colors"
                    >
                      {t('nav.contact', 'Contact')}
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-base sm:text-lg">{t('footer.contact', 'Contact')}</h3>
            <div className="space-y-2 sm:space-y-3 text-sm sm:text-base text-muted-foreground">
              {(footerSettings?.contact_info?.address || profile?.location) && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span>{footerSettings?.contact_info?.address || getLocalized(profile?.location)}</span>
                </div>
              )}
              {(footerSettings?.contact_info?.email || profile?.email) && (
                <div className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                  <a
                    href={`mailto:${footerSettings?.contact_info?.email || profile?.email}`}
                    className="hover:text-primary transition-colors break-all"
                  >
                    {footerSettings?.contact_info?.email || profile?.email}
                  </a>
                </div>
              )}
              {(footerSettings?.contact_info?.phone || profile?.phone) && (
                <div className="flex items-center gap-2">
                  <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                  <a
                    href={`tel:${footerSettings?.contact_info?.phone || profile?.phone}`}
                    className="hover:text-primary transition-colors"
                  >
                    {footerSettings?.contact_info?.phone || profile?.phone}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-muted-foreground">
          <p>
            {(() => {
              const text = getLocalized(footerSettings?.copyright_text);
              if (text) {
                // If text contains a year like 2024 or 2025, update it to current year
                // This ensures even if they hardcoded a year in settings, it shows current year
                return text.replace(/\b20\d{2}\b/g, currentYear.toString());
              }
              return `© ${currentYear} ${profile?.display_name || "Photography"}. ${t('footer.rightsReserved', 'All rights reserved.')}`;
            })()}
          </p>
        </div>
      </div>
    </footer>
  );
}